import * as React from 'react'
import {ReactElement} from 'react'
import {Box} from '@mui/material'
import {Messages} from '../../../../Messages'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {Nullable} from '../../../../app/TypeHelpers'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {PageTitle} from '../../applications/common/resident/NatResidentApplicationsCommon'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {NatPostOldMemberRequest, NatPostOldMemberResponse} from '../repository/NatPostOldMemberRequest'
import {NatOwnershipTransfersRepo} from '../repository/NatOwnershipTransfersRepo'
import {NatOwnershipTransferOldMemberLocationState} from './oldMember/NatGuestOldMemberInterface'
import NatGuestOldMemberForm from './oldMember/NatGuestOldMemberForm'
import {
    createGuestWithErrorHandlingPage,
    GuestWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createGuestWithErrorHandlingPage'
import {GuestPlainLinkStyledBackButton} from '../common/NatOwnershipTransfersCommon'
import {Location} from 'react-router-dom'


interface Props {
    condominiumCd: string
    applyId?: string
    ownershipTransfersRepo: NatOwnershipTransfersRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatOwnershipTransferOldMemberLocationState>
}

interface State {
    familyName: Nullable<string>
    firstName: Nullable<string>
    moveOutDate: Nullable<string>
    phoneNumberAfterMoveOut: Nullable<string>
    zipCodeAfterMoveOut: Nullable<string>
    prefectureAfterMoveOut: Nullable<string>
    address2AfterMoveOut: Nullable<string>
    address3AfterMoveOut: Nullable<string>
    buildingRoomNumberAfterMoveOut: Nullable<string>
    mailAddress: Nullable<string>
    mailAddressConfirm: Nullable<string>
    version: Nullable<number>
    isLoadCompleted: boolean
}

class NatGuestOldMemberInputPage
    extends React.Component<Props & GuestWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & GuestWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            familyName: null,
            firstName: null,
            moveOutDate: null,
            phoneNumberAfterMoveOut: null,
            zipCodeAfterMoveOut: null,
            prefectureAfterMoveOut: null,
            address2AfterMoveOut: null,
            address3AfterMoveOut: null,
            buildingRoomNumberAfterMoveOut: null,
            mailAddress: null,
            mailAddressConfirm: null,
            version: 0,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
        this.onClickBackButton = this.onClickBackButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const applyInfo = this.props.location.state?.applyInfo

        this.setState({
            familyName: applyInfo?.oldMemberFamilyName ?? null,
            firstName: applyInfo?.oldMemberFirstName ?? null,
            moveOutDate: applyInfo?.moveOutDate ?? null,
            phoneNumberAfterMoveOut: applyInfo?.phoneNumberAfterMoveOut ?? null,
            zipCodeAfterMoveOut: applyInfo?.zipCodeAfterMoveOut ?? null,
            prefectureAfterMoveOut: applyInfo?.prefectureAfterMoveOut ?? null,
            address2AfterMoveOut: applyInfo?.address2AfterMoveOut ?? null,
            address3AfterMoveOut: applyInfo?.address3AfterMoveOut ?? null,
            buildingRoomNumberAfterMoveOut: applyInfo?.buildingRoomNumberAfterMoveOut ?? null,
            mailAddress: applyInfo?.oldMemberMailAddress ?? null,
            mailAddressConfirm: null,
            version: applyInfo?.version ?? null,
            isLoadCompleted: true,
        })
    }

    onClickBackButton(): void {
        this.props.navigate(
            `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/old-member/paymentChangeDateConfirm`,
            {replace: true},
        )
    }

    async onClickApplyConfirmButton(request: NatPostOldMemberRequest): Promise<void> {
        const {ownershipTransfersRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPostOldMemberResponse> =
                await this.props.asyncCallFunction(
                    ownershipTransfersRepo.applyOwnershipTransferOldMember.bind(
                        ownershipTransfersRepo, condominiumCd, request),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                    },
                    {
                        defaultErrorTitle: Messages.EOPCR0014.toString(),
                        defaultErrorTitlePriority: true,
                        replacementDetailErrorMessages: [{
                            targetMessageId: Messages.EOPCA0004,
                            replacementMessageId: Messages.EOPCR0015,
                        }],
                    },
                )

            if (response && response.requestError === null) {
                const locationState: NatOwnershipTransferOldMemberLocationState = {
                    applyInfo: {
                        oldMemberFamilyName: request.familyName,
                        oldMemberFirstName: request.firstName,
                        moveOutDate: request.moveOutDate,
                        phoneNumberAfterMoveOut: request.phoneNumberAfterMoveOut,
                        zipCodeAfterMoveOut: request.zipCodeAfterMoveOut,
                        prefectureAfterMoveOut: request.prefectureAfterMoveOut,
                        address2AfterMoveOut: request.address2AfterMoveOut,
                        address3AfterMoveOut: request.address3AfterMoveOut,
                        buildingRoomNumberAfterMoveOut: request.buildingRoomNumberAfterMoveOut,
                        oldMemberMailAddress: request.mailAddress,
                        oldMemberMailAddressConfirm: request.mailAddressConfirm,
                        version: request.version,
                    },
                    ownershipTransferDate: this.props.location.state.ownershipTransferDate,
                    isApply: true,
                }
                this.props.navigate(
                    `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/old-member/confirm/${this.props.applyId ?? ''}`,
                    {
                        replace: true,
                        state: JSON.parse(JSON.stringify(locationState)),
                    },
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <GuestPlainLinkStyledBackButton onClick={this.onClickBackButton}>戻る</GuestPlainLinkStyledBackButton>
                <BlockSpacer height={'15px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    組合員資格変更届
                </PageTitle>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatGuestOldMemberForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                    onClickBackButton={this.onClickBackButton}
                />
            </>
        )
    }
}

export default createGuestWithErrorHandlingPage(NatGuestOldMemberInputPage)
